/**
 * Push-subscription pure helpers — Phase 6 / M5.3 / M5.4 client side.
 *
 * Wraps the browser PushManager + server endpoints in small functions
 * with no Svelte / runtime dependencies. The component layer
 * (NotificationToggle.svelte) and bootstrap (sw-registration.ts) call
 * these; tests mock fetch + the PushManager interface.
 *
 * Server contract (controllers in v2/services/messages/src/push/):
 *   GET    /api/me/push-subscriptions/vapid-public-key  → { vapidPublicKey }
 *   POST   /api/me/push-subscriptions                   ← { endpoint, keys:{p256dh,auth}, userAgent? }
 *   DELETE /api/me/push-subscriptions                   ← { endpoint }
 *
 * All endpoints require the CRM session cookie (same gate the rest of
 * the widget uses). Calls use `credentials: 'include'`.
 */

import { http } from '../api/http.js';

/**
 * The field is `vapidPublicKey`, not `publicKey`. Until M5.6.5 this
 * interface said `publicKey`, so `fetchVapidKey` threw on every call and
 * the widget never completed a push subscription — silently, because the
 * only caller catches. The spec passed because it mocked the wrong shape
 * too. See vapid-public-key.controller.ts for the authority.
 */
export interface VapidKeyResponse {
  vapidPublicKey: string;
}

export interface PushKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionBody {
  endpoint: string;
  keys: PushKeys;
  userAgent?: string;
}

/**
 * Read the server's VAPID public key. Fetched once per subscribe attempt —
 * it's stable for the life of the server's keypair, so callers cache the
 * result in component state if they want to.
 */
export async function fetchVapidKey(): Promise<string> {
  const res = await http.get<VapidKeyResponse>('/api/me/push-subscriptions/vapid-public-key');
  if (!res?.vapidPublicKey) {
    throw new Error('VAPID public key endpoint returned no vapidPublicKey');
  }
  return res.vapidPublicKey;
}

/**
 * Convert the base64url-encoded VAPID public key the server returns into
 * the Uint8Array the PushManager.subscribe expects as
 * `applicationServerKey`. Base64url uses `-_` instead of `+/` and may
 * omit `=` padding — decode by translating + padding before atob().
 */
export function vapidKeyToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Extract the over-the-wire fields from a browser PushSubscription. The
 * PushSubscription.getKey('p256dh' | 'auth') return ArrayBuffer; the
 * server expects URL-safe base64 strings.
 */
export function extractPushKeys(sub: PushSubscription): PushKeys {
  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!p256dh || !auth) {
    throw new Error('PushSubscription missing p256dh / auth keys');
  }
  return {
    p256dh: arrayBufferToBase64Url(p256dh),
    auth: arrayBufferToBase64Url(auth),
  };
}

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Subscribe via the browser PushManager. Caller is responsible for first
 * ensuring `Notification.permission === 'granted'` AND that a Service
 * Worker is registered (`navigator.serviceWorker.ready`).
 */
export async function subscribeBrowser(
  swReg: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription> {
  return swReg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidKeyToUint8Array(vapidPublicKey),
  });
}

/**
 * POST the subscription to the server. Server is idempotent — same
 * endpoint upserts the existing row instead of creating duplicates.
 */
export async function postSubscription(body: PushSubscriptionBody): Promise<void> {
  await http.post('/api/me/push-subscriptions', body);
}

/**
 * DELETE the subscription server-side. The browser-side
 * `subscription.unsubscribe()` should happen first; this is the cleanup
 * for our DB row.
 */
export async function deleteSubscription(endpoint: string): Promise<void> {
  await http.delete('/api/me/push-subscriptions', { endpoint });
}

/**
 * Idempotent "make sure we have a server-side subscription" — called on
 * every widget mount where `Notification.permission === 'granted'`.
 *
 * Browser may have silently rotated the endpoint (pushsubscriptionchange);
 * server may have pruned the row on a 410. Either way, this resubscribes
 * + reposts if the local PushManager has no subscription, and is a
 * cheap no-op otherwise. Returns the active endpoint, or null when
 * permission isn't granted (caller filters).
 */
export async function ensureSubscription(
  swReg: ServiceWorkerRegistration,
): Promise<string | null> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return null;
  }
  const existing = await swReg.pushManager.getSubscription();
  if (existing) {
    // Hot path: subscription already in PushManager. Don't re-POST — the
    // server upsert is idempotent but firing it on every mount is noisy.
    return existing.endpoint;
  }
  const vapidKey = await fetchVapidKey();
  const fresh = await subscribeBrowser(swReg, vapidKey);
  await postSubscription({
    endpoint: fresh.endpoint,
    keys: extractPushKeys(fresh),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
  return fresh.endpoint;
}
